/**
 * `gamification-core` — CONFIG for the bible-strong domain (Scripture reading).
 *
 * OPT-IN and NON-punitive retrofit: gamification rewards ENGAGEMENT with
 * Palabra (constancia de lectura, notas, subrayados, marcadores) como aliento,
 * never as pressure. Private leagues by default (§leagues). The engine is
 * agnostic; here we only declare domain rules.
 *
 * READING SIGNAL (streak): this app does NOT have an `activity` substrate or an
 * explicit "chapter read" log. The streak is DERIVED as a PROXY from the
 * annotation activity available LOCALLY — the union of notes, highlights
 * y marcadores (`bible_note` + `bible_highlight` + `bible_verse_highlight` +
 * `bible_bookmark`) — where each local day with at least one annotation counts
 * as a reading day. It is NON-invasive: adds no tables, touches no sync rules nor
 * el servidor. Ver `derive.ts`.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Domain event types (derived from local annotations). */
export const BSO_EVENTS = {
  noteCreated: "note.created",
  highlightCreated: "highlight.created",
  bookmarkCreated: "bookmark.created",
  planDayCompleted: "plan-day.completed",
} as const;

/** Streak line: days with reading/annotation activity (consistency). */
export const READING_STREAK_LINE = "reading";

/** Consistency badge bonus XP (defined here for test reuse). */
export const DEVOTED_READER_BONUS_XP = 50;
export const FAITHFUL_MONTH_BONUS_XP = 100;
/** Daily quest bonus (once per day when writing a note). */
export const DAILY_READING_BONUS_XP = 15;

export const bsoGamificationConfig: GamificationConfig = {
  xp: {
    [BSO_EVENTS.noteCreated]: 10,
    [BSO_EVENTS.highlightCreated]: 5,
    [BSO_EVENTS.bookmarkCreated]: 3,
    [BSO_EVENTS.planDayCompleted]: 5,
  },
  // ANY annotation marks its day as a reading day -> extends the streak.
  streakLines: {
    [BSO_EVENTS.noteCreated]: [READING_STREAK_LINE],
    [BSO_EVENTS.highlightCreated]: [READING_STREAK_LINE],
    [BSO_EVENTS.bookmarkCreated]: [READING_STREAK_LINE],
    [BSO_EVENTS.planDayCompleted]: [READING_STREAK_LINE],
  },
  counters: {
    [BSO_EVENTS.noteCreated]: ["notes"],
    [BSO_EVENTS.highlightCreated]: ["highlights"],
    [BSO_EVENTS.bookmarkCreated]: ["bookmarks"],
    [BSO_EVENTS.planDayCompleted]: ["planDays"],
  },
  badges: [
    {
      id: "first-note",
      title: "First note",
      description: "Write your first study note.",
      when: counterAtLeast("notes", 1),
    },
    {
      id: "devoted-reader",
      title: "Devoted reader",
      description: "Read or annotate seven days in a row.",
      when: streakAtLeast(READING_STREAK_LINE, 7),
      bonusXp: DEVOTED_READER_BONUS_XP,
    },
    {
      id: "faithful-month",
      title: "Faithful month",
      description: "Keep a thirty-day reading streak.",
      when: streakAtLeast(READING_STREAK_LINE, 30),
      bonusXp: FAITHFUL_MONTH_BONUS_XP,
    },
    {
      id: "scholar",
      title: "Scholar",
      description: "Write fifty study notes.",
      when: counterAtLeast("notes", 50),
    },
  ],
  quests: [
    {
      id: "daily-reading",
      title: "Spend time in the Word today",
      period: "daily",
      goals: [{ metric: BSO_EVENTS.noteCreated, target: 1 }],
      bonusXp: DAILY_READING_BONUS_XP,
    },
  ],
};
